import { useOrderCreateStore } from 'modules/create-order/model/create-order.store'
import { storeToRefs } from 'pinia'
import { PageName } from 'shared/lib/types/app/pages'
import { getTokenCookie } from 'shared/lib/utils/cookies'
import { createRouter, createWebHistory } from 'vue-router'

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: PageName.MAIN_LAYOUT,
      meta: {
        breadcrumbName: 'common.mainPage',
      },
      component: () => import('shared/ui/layouts/MainLayout.vue'),
      redirect: { name: PageName.ALL_ASSIGNS, query: { tab: 'allAssigns' } },
      children: [],
    },
    {
      path: '/login',
      name: PageName.LOGIN_LAYOUT,
      component: () => import('shared/ui/layouts/LoginLayout.vue'),
    },
  ],
})

router.beforeEach((to, from, next) => {
  if (!getTokenCookie() && to.name !== PageName.LOGIN_PAGE)
    return next({ name: PageName.LOGIN_PAGE })

  // Проверяем, есть ли meta.saveQueryFor, формируем конфиг для фильтрации и переадресации
  if (to.name && Array.isArray(from.meta.saveQueryFor)) {
    const queryConfig = from.meta.saveQueryFor.find(
      (item: { targetPageName: string }) => item.targetPageName === to.name,
    )

    // Если есть совпадение с целевой страницей
    if (queryConfig && JSON.stringify(to.query) === '{}') {
      const filteredQuery = Object.keys(from.query)
        .filter(key => queryConfig.targetQueryKeys.includes(key))
        .reduce((acc, key) => {
          acc[key] = from.query[key] // прокидываем значения отфильтрованным ключам
          return acc
        }, {} as Record<string, any>)

      return next({
        name: to.name,
        params: to.params,
        query: filteredQuery, // Передаем только отфильтрованные query
      })
    }
  }

  if (to.name === PageName.CREATED_ORDER) {
    const { accessToEnter } = storeToRefs(useOrderCreateStore())
    if (!accessToEnter.value)
      return next({ name: PageName.ALL_ASSIGNS, query: { tab: 'allAssigns' } })

    return next()
  }

  // Если параметры уже есть или их передача не требуется
  return next()
})

/*
 * Устаревшая вкладка после деплоя. Страницы грузятся лениво (`() => import(...)`), а у новой сборки
 * другие имена файлов — старая вкладка просит файлы, которых на сервере уже нет, и переход падает
 * («Failed to fetch dynamically imported module»). Тогда перезагружаем страницу сразу по адресу,
 * куда шёл переход, — подтянется новая сборка.
 * - `vite:preloadError` Vite шлёт при любом сбое ленивой загрузки (не зависит от текста ошибки браузера);
 *   событие есть только в сборке, в `pnpm dev` не срабатывает.
 * - Адрес подменяем и вызываем reload(), а не location.assign(): при перезагрузке браузер сверяет
 *   index.html с сервером, а переход по адресу мог бы взять старый из кэша (nginx: expires 3m).
 * - Не чаще раза в 10 с: если файла правда нет (сломанный деплой), не уходим в бесконечную перезагрузку.
 */
const CHUNK_RELOAD_KEY = 'chunk-reload-at'
const CHUNK_RELOAD_PAUSE_MS = 10_000
let isChunkLoadFailed = false

window.addEventListener('vite:preloadError', () => {
  isChunkLoadFailed = true
})

router.onError((_error, to) => {
  if (!isChunkLoadFailed)
    return
  isChunkLoadFailed = false

  try {
    const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY))
    if (Date.now() - lastReloadAt < CHUNK_RELOAD_PAUSE_MS)
      return
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
  }
  catch {
    // без sessionStorage не защититься от цикла — не перезагружаем, ошибка покажется как раньше
    return
  }

  window.history.replaceState(window.history.state, '', router.resolve(to).href)
  window.location.reload()
})
